import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  TextField,
} from "@mui/material";
import { getPositionFromWatch } from "../../../helpers/position";
import { useGetLocations, usePlaceSearch } from "./hooks";
import { useAppDispatch, useAppSelector } from "../../../redux-store";
import {
  loadSavedLocations,
  actions,
} from "../../../ducks/data/saved-locations";
import {
  selectFavoriteLocations,
  selectTerminalLocations,
} from "../../../ducks/data/saved-locations/selectors";
import SearchResultOption from "./SearchResultOption";
import mapSavedLocationToSearchOption from "./map-saved-location-to-search-option";
import { SearchOption } from "./SearchOption";
import { distanceFromPoint } from "./use-sort-by-closeness-to-driver-location";
import { SavedLocations } from "../../../ducks/data/saved-locations/types";
import sortBy from "lodash/sortBy";
import lowerCaseMatch from "../../../helpers/lower-case-match";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import useFeature from "../../../hooks/useFeature";
import useDefaultSearchLocation from "./use-default-search-location";

interface PlaceSearchProps {
  label: string;
  location: SearchOption | null;
  onLocationChange: (loc: null | SearchOption) => void;
  onLocationChangedManually: () => void;
}

const getOptionLabel = (option: SearchOption) => {
  switch (option.type) {
    case "SEARCH_RESULT":
      return option.value.data.title.label;
    case "SAVED_LOCATION":
      return option.value.data.customName;
    case "REVERSE_GEOCODE_OPTION":
      return option.value.data.label;
    default:
      return "";
  }
};

export type InnerPlaceSearchProps = PlaceSearchProps & {
  savedFavorites: SavedLocations;
  savedTerminals: SavedLocations;
};

export const InnerPlaceSearch = ({
  label,
  location,
  onLocationChange,
  onLocationChangedManually,
  savedFavorites,
  savedTerminals,
}: InnerPlaceSearchProps) => {
  const { t } = useTranslation("gps");
  const placeSearchWithTimeoutEnabled = useFeature(
    "mobile-app-place-search-with-timeout"
  );
  const defaultSearchLocation = useDefaultSearchLocation();
  const [dirty, setDirty] = useState(false);
  const [searchState, setSearchState] = useState<{
    inputValue: string;
    results: SearchOption[];
  }>({ inputValue: "", results: [] });
  const { inputValue, results } = searchState;
  const [startLocations, setStartLocations] = useState<SearchOption[]>([]);
  const [searchLocationState, setSearchLocationState] = useState<
    "LOADING" | "ERROR" | "LOADED"
  >("LOADING");
  const [searchLocation, setSearchLocation] = useState<{
    lat: number;
    lon: number;
  }>(defaultSearchLocation);
  const suggest = usePlaceSearch();
  const getLocations = useGetLocations({
    timeout: placeSearchWithTimeoutEnabled ? 15000 : null,
  });

  const [filteredSavedTerminals, filteredSavedFavorites] = useMemo(() => {
    if (!inputValue.trim()) {
      return [
        sortBy(
          savedTerminals.map(mapSavedLocationToSearchOption),
          distanceFromPoint(searchLocation)
        ).slice(0, 3),
        sortBy(
          savedFavorites.map(mapSavedLocationToSearchOption),
          distanceFromPoint(searchLocation)
        ).slice(0, 3),
      ];
    } else {
      return [
        sortBy(
          savedTerminals
            .filter((t) => lowerCaseMatch(inputValue, t.customName))
            .map(mapSavedLocationToSearchOption),
          distanceFromPoint(searchLocation)
        ),
        sortBy(
          savedFavorites
            .filter((t) => lowerCaseMatch(inputValue, t.customName))
            .map(mapSavedLocationToSearchOption),
          distanceFromPoint(searchLocation)
        ),
      ];
    }
  }, [savedTerminals, savedFavorites, inputValue, searchLocation]);

  useEffect(() => {
    const inputValue = location ? getOptionLabel(location) : "";
    setSearchState((state) => ({
      inputValue,
      results: inputValue === state.inputValue ? state.results : [],
    }));
  }, [location]);

  const getPositionAndSetSearchLocation = async () => {
    getPositionFromWatch({ timeout: placeSearchWithTimeoutEnabled ? 10000 : null }).then(
      (loc) => {
        if (loc) {
          setSearchLocationState("LOADED");
          setSearchLocation(loc);
        } else {
          setSearchLocation(defaultSearchLocation);
          setSearchLocationState("ERROR");
        }
      }
    );
  };

  useEffect(() => {
    getPositionAndSetSearchLocation();
  }, []);

  const onRetryLocationClick = () => {
    setSearchLocationState("LOADING");
    getPositionAndSetSearchLocation();
  };

  const onInputChange = (
    e: any,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    const inputValue = value.trim();
    setSearchState((state) => {
      const results = inputValue ? state.results : [];
      return { inputValue, results };
    });
    // Only what the user typed goes to Google. A "reset" (the text of a
    // picked or prefilled place) would start a new autocomplete session that
    // never ends in a place lookup, which Google bills per request.
    if (inputValue && reason === "input") {
      setDirty(true);
      suggest(
        inputValue,
        {
          type: "AT",
          position: searchLocation,
        },
        (err, data) => {
          if (err) {
            // Already reported, with its type, by the GooglePlaceSearch
            // onError in places-context; err itself serializes to {}.
            return;
          } else {
            setSearchState((currentState) => {
              if (currentState.inputValue !== inputValue) {
                return currentState;
              }
              return {
                inputValue,
                results: (data || []).map((x) => ({
                  type: "SEARCH_RESULT",
                  id: x.id,
                  value: { id: x.id, data: x, locationType: "SEARCH_RESULT" },
                })),
              };
            });
          }
        }
      );
    }
  };

  const onChange = async (_: unknown, newValue: SearchOption | null) => {
    onLocationChangedManually();
    onLocationChange(newValue);
  };

  // Don't know if there's a better way. If the parent component does not use
  // useCallback to stabilise the onLocationChange event handler (and we
  // correctly add it as a dependency to the useEffect hook), then the result
  // would be that we would trigger a new reverse geocode call on every
  // rerender.
  const onLocationChangeRef = useRef<typeof onLocationChange>(onLocationChange);
  onLocationChangeRef.current = onLocationChange;

  useEffect(() => {
    getLocations().then(async (locations) => {
      if (locations.length) {
        const mappedLocations = locations.map(
          (location): SearchOption => ({
            type: "REVERSE_GEOCODE_OPTION",
            value: {
              id: location.id,
              locationType: "REVERSE_GEOCODE_OPTION",
              data: location,
            },
          })
        );
        setStartLocations(mappedLocations);
        onLocationChangeRef.current(mappedLocations[0]);
      }
    });
  }, [getLocations, onLocationChangeRef]);

  const allOptions = useMemo(
    () => [
      ...filteredSavedTerminals.slice(0, 2),
      ...filteredSavedFavorites.slice(0, 2),
      ...(!dirty ? startLocations : results),
    ],
    [
      dirty,
      filteredSavedTerminals,
      filteredSavedFavorites,
      results,
      startLocations,
    ]
  );

  const [options, filterSelectedOptions] =
    // If _something_ is selected, and the selected value is not part of the
    // current values, add it, but hide it (as the currently selected value
    // MUST be an element in this list)
    location && !allOptions.includes(location)
      ? [[location, ...allOptions], true]
      : [allOptions, false];

  return (
    <>
      {placeSearchWithTimeoutEnabled && searchLocationState === "ERROR" && (
        <div className="text-red-500 justify-between flex items-center text-sm mt-4">
          <span>{t("errorFetching")}</span>
          <Button
            className="flex gap-2 items-center"
            variant="contained"
            onClick={onRetryLocationClick}
          >
            <RefreshIcon />
            <span>{t("retry")}</span>
          </Button>
        </div>
      )}
      {placeSearchWithTimeoutEnabled && searchLocationState === "LOADING" && (
        <div className="text-sm mt-6 mb-3">{t("loadingLocation")}</div>
      )}
      <Autocomplete
        getOptionLabel={getOptionLabel}
        options={options}
        filterOptions={(x) => x}
        autoSelect
        selectOnFocus
        clearOnBlur
        clearOnEscape
        autoHighlight
        filterSelectedOptions={filterSelectedOptions}
        includeInputInList
        value={location}
        onChange={onChange}
        onInputChange={onInputChange}
        renderInput={(params) => (
          <TextField
            name="address"
            {...params}
            inputProps={{
              ...params.inputProps,
              autoComplete: "disabled",
            }}
            label={label}
            fullWidth
            type="text"
            margin="dense"
          />
        )}
        renderOption={(p, o) => (
          <li {...p} key={o.value.id}>
            <SearchResultOption option={o} />
          </li>
        )}
      />
    </>
  );
};

const PlaceSearch = (props: PlaceSearchProps) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(loadSavedLocations());
    return () => {
      dispatch(actions.clearSavedLocations());
    };
  }, [dispatch]);
  const savedTerminals = useAppSelector(selectTerminalLocations);
  const savedFavorites = useAppSelector(selectFavoriteLocations);
  return (
    <InnerPlaceSearch
      {...props}
      savedTerminals={savedTerminals}
      savedFavorites={savedFavorites}
    />
  );
};

export default PlaceSearch;
